const FALLBACK_COLORS = [
    '#F59E0B',
    '#14B8A6',
    '#3B82F6',
    '#EC4899',
    '#8B5CF6',
    '#10B981',
    '#F97316',
    '#06B6D4',
]

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)))

const padHex = (value) => clampChannel(value).toString(16).padStart(2, '0')

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return null

    const cleaned = hex.trim().replace('#', '')
    if (![3, 6].includes(cleaned.length) || /[^0-9a-f]/i.test(cleaned)) return null

    const normalized =
        cleaned.length === 3
            ? cleaned
                  .split('')
                  .map((char) => char + char)
                  .join('')
            : cleaned

    return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
    }
}

const rgbToHex = ({ r, g, b }) => `#${padHex(r)}${padHex(g)}${padHex(b)}`

const mix = (hex, targetHex, weight = 0.5) => {
    const base = hexToRgb(hex)
    const target = hexToRgb(targetHex)
    if (!base || !target) return hex

    const safeWeight = Math.max(0, Math.min(1, weight))

    return rgbToHex({
        r: base.r + (target.r - base.r) * safeWeight,
        g: base.g + (target.g - base.g) * safeWeight,
        b: base.b + (target.b - base.b) * safeWeight,
    })
}

const getFallbackColor = (project) => {
    const key = String(project?.id ?? project?.name ?? project?.title ?? 'project')
    const hash = key.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
    return FALLBACK_COLORS[hash % FALLBACK_COLORS.length]
}

export const normalizeProjectColor = (rawColor, fallbackProject) => {
    const parsed = hexToRgb(rawColor)
    return parsed ? rgbToHex(parsed) : getFallbackColor(fallbackProject)
}

export const alphaColor = (hex, alpha) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return `rgba(17,17,17,${alpha})`
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

const getReadableText = (hex) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return '#1F2937'

    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
    return brightness > 150 ? '#1F2937' : '#FFFFFF'
}

export const getProjectColorTokens = (project) => {
    const accent = normalizeProjectColor(project?.color, project)

    return {
        accent,
        accentSoft: alphaColor(accent, 0.14),
        accentSurface: `linear-gradient(135deg, ${alphaColor(accent, 0.2)} 0%, ${alphaColor(
            mix(accent, '#FFFFFF', 0.35),
            0.12
        )} 100%)`,
        accentBorder: alphaColor(accent, 0.35),
        accentText: mix(accent, '#111827', 0.35),
        accentStrongText: getReadableText(accent),
        accentMuted: mix(accent, '#FFFFFF', 0.72),
    }
}
